<template>
    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <StickyContainer
        id="viewerToolbar"
        v-resize.immediate="onResize"
        :offset="topBarHeight - 1"
        :class="['bg-white border-slate-200 text-slate-600 border-t border-b']"
    >
        <div class="menu">
            <div class="container menu-container h-full flex flex-row items-center text-sm">
                <div
                    :class="[
                        'flex flex-row items-center w-full justify-between',
                        { 'pointer-events-none disabled': actionsDisabled },
                    ]"
                >
                    <div class="flex flex-row items-stretch w-full lg:w-auto justify-center md:justify-between px-0">
                        <div class="hidden md:flex flex-row items-center justify-center">
                            <Btn
                                id="next"
                                variant="text"
                                :class="['pageDown', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Next Page"
                                tabindex="14"
                                data-l10n-id="next"
                                icon="arrow-down"
                                compact
                                @click="pageNext"
                            />
                            <Btn
                                id="previous"
                                variant="text"
                                :class="['pageUp', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Previous Page"
                                tabindex="13"
                                data-l10n-id="previous"
                                icon="arrow-up"
                                compact
                                @click="pagePrevious"
                            />
                        </div>
                        <div
                            :class="[
                                'flex flex-row items-center border-r border-solid border-slate-200 px-2 md:px-5 text-xs md:text-base ',
                                actionsDisabled ? 'text-slate-300' : 'text-slate-600',
                            ]"
                        >
                            <input
                                id="pageNumber"
                                data-testid="document-page-count"
                                :class="[
                                    'mr-2 my-2 px-1 text-center w-10 md:w-15',
                                    {
                                        'text-slate-400': actionsDisabled,
                                        'protected-auth': actionsShouldPromptSignUp,
                                    },
                                ]"
                                :readonly="actionsDisabled"
                                type="number"
                                value="1"
                                size="4"
                                min="1"
                                tabindex="15"
                                @change="pageNumberChanged"
                            />
                            <div
                                v-if="document.number_of_pages"
                                class="w-16"
                            >
                                {{ $t('show.toolbar.page_article') }}
                                <span class="ml-1">{{ document.number_of_pages }}</span>
                            </div>
                        </div>
                        <div
                            class="hidden md:flex flex-row items-center border-r border-solid border-slate-200 h-auto px-4"
                        >
                            <Btn
                                variant="text"
                                :disabled="actionsDisabled"
                                :class="['zoomOut', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Zoom Out"
                                tabindex="21"
                                data-l10n-id="zoom_out"
                                icon="zoom-out"
                                compact
                                @click="zoomOut"
                            />
                            <Btn
                                variant="text"
                                :disabled="actionsDisabled"
                                :class="['zoomIn', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Zoom In"
                                tabindex="22"
                                data-l10n-id="zoom_in"
                                icon="zoom"
                                compact
                                @click="zoomIn"
                            />
                        </div>
                        <div
                            :class="[
                                'flex flex-row items-center justify-center md:flex-grow lg:flex-grow-0 border-r border-solid border-slate-200 h-auto px-2 md:px-4 text-xs md:text-base leading-normal space-x-2',
                                { 'text-slate-400': actionsDisabled },
                            ]"
                        >
                            <ToggleSwitch
                                id="show-markings"
                                v-model="showMarkings"
                                :disabled="actionsDisabled"
                                :class="['mr-3', { 'protected-auth': actionsShouldPromptSignUp }]"
                            >
                            </ToggleSwitch>
                            <span
                                class="hidden md:block"
                                style="min-width: 150px"
                            >
                                {{ $t('show.toolbar.label_show_markings') }}
                            </span>
                            <span
                                class="block md:hidden"
                                style="min-width: 40px"
                            >
                                {{ $t('show.toolbar.label_show_markings_sm') }}
                            </span>
                        </div>
                        <div
                            class="flex flex-row items-center lg:border-r lg:border-solid lg:border-slate-200 h-auto pr-0 pl-2 md:px-5"
                        >
                            <Btn
                                id="rotate"
                                variant="text"
                                :disabled="actionsDisabled"
                                :class="['rotate', { 'protected-auth': actionsShouldPromptSignUp }]"
                                title="Rotate"
                                tabindex="15"
                                data-l10n-id="rotate"
                                icon="rotate-clockwise"
                                compact
                                @click="rotateDocument"
                            />
                        </div>
                    </div>
                    <div
                        id="findbar"
                        class="findbar hidden lg:flex flex-grow justify-end"
                    >
                        <div class="flex flex-row w-full justify-end">
                            <div class="relative my-2 max-w-md pl-4 w-full">
                                <input
                                    id="findInput"
                                    :placeholder="$t('show.toolbar.placeholder_find_in_document')"
                                    tabindex="91"
                                    :class="['pr-12 py-2', { 'protected-auth': actionsShouldPromptSignUp }]"
                                />
                                <span
                                    :class="[
                                        'absolute leading-none btn-search',
                                        actionsDisabled ? 'cursor-default' : 'cursor-pointer',
                                    ]"
                                    style="right: 1rem; top: 0.4rem"
                                >
                                    <i
                                        :class="[
                                            'icon icon-search w-7 h-7',
                                            {
                                                'text-slate-400': actionsDisabled,
                                                'protected-auth': actionsShouldPromptSignUp,
                                            },
                                        ]"
                                    ></i>
                                </span>
                            </div>
                            <div class="hidden">
                                <button
                                    id="findPrevious"
                                    class="findPrevious"
                                    title=""
                                    tabindex="92"
                                    data-l10n-id="find_previous"
                                >
                                    <span data-l10n-id="find_previous_label">Previous</span>
                                </button>
                                <button
                                    id="findNext"
                                    class="findNext"
                                    title=""
                                    tabindex="93"
                                    data-l10n-id="find_next"
                                >
                                    <span data-l10n-id="find_next_label">Next</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div id="headerLoadingBar">
                <div class="progress">
                    <div class="glimmer"></div>
                </div>
            </div>
        </div>
    </StickyContainer>
    <!-- eslint-disable vue/no-bare-strings-in-template -->
</template>

<script>
import { round } from 'lodash-es';
import { mapGetters } from 'vuex';
import ToggleSwitch from '@components/ToggleSwitch';
import StickyContainer from '@/components/StickyContainer';

const { PDFViewerApplication } = window;
export default {
    name: 'DocumentPDFViewerToolbar',
    i18nOptions: {
        namespaces: ['document'],
    },
    components: {
        ToggleSwitch,
        StickyContainer,
    },
    data() {
        return {
            showMarkings: true,
        };
    },
    computed: {
        ...mapGetters({
            document: 'document/currentDocument',
            pdfViewerInitialized: 'document/pdfViewerInitialized',
            isLoggedIn: 'auth/isLoggedIn',
            topBarHeight: 'ui/topBarHeight',
            user: 'auth/user',
        }),
        actionsDisabled() {
            return !this.pdfViewerInitialized;
        },
        actionsShouldPromptSignUp() {
            return !this.isLoggedIn;
        },
    },
    watch: {
        showMarkings(newValue) {
            this.$store.dispatch('document/toggleMarkings', newValue);
        },
    },
    mounted() {
        this.$bus.on('toggle-mark', (data) => {
            this.showMarkings = true;
        });
    },
    methods: {
        onResize(el) {
            this.$store.commit('document/SET_TOOLBAR_HEIGHT', el.offsetHeight);
        },
        rotateDocument() {
            PDFViewerApplication.rotatePages(90);
            this.$store.dispatch('document/rotateDocument', PDFViewerApplication.pageRotation).then(this.forceResize);
        },
        zoomOut() {
            PDFViewerApplication.zoomOut();
            this.forceResize();
        },
        zoomIn() {
            PDFViewerApplication.zoomIn();
            this.forceResize();
        },
        pagePrevious() {
            // eslint-disable-next-line no-plusplus
            PDFViewerApplication.page--;
        },
        pageNext() {
            // eslint-disable-next-line no-plusplus
            PDFViewerApplication.page++;
        },
        pageNumberChanged(event) {
            // Handle the user inputting a floating point number.
            const newPage = round(event.target.value);

            if (newPage < 1) {
                PDFViewerApplication.page = 1;
            } else if (newPage > PDFViewerApplication.pagesCount) {
                PDFViewerApplication.page = PDFViewerApplication.pagesCount;
            } else {
                PDFViewerApplication.page = newPage;
            }

            if (event.target.value !== newPage.toString()) {
                // eslint-disable-next-line no-param-reassign
                event.target.value = PDFViewerApplication.page;
            }
        },
        forceResize() {
            const resizeEvent = window.document.createEvent('UIEvents');
            resizeEvent.initUIEvent('resize', true, false, window, 0);
            window.dispatchEvent(resizeEvent);
            PDFViewerApplication.forceRendering();
        },
    },
};
</script>

<style lang="scss" scoped>
#viewerToolbar {
    z-index: 9;

    input {
        padding-bottom: 1px;
        padding-top: 1px;
    }
    &:not(.is-sticky) {
        @apply z-[8];
    }

    button {
        @apply flex px-2 py-1;
    }
}

#findInput[data-status='pending'] {
    background-image: none;
}

#findInput.notFound {
    @apply border-red-100;
}

#pageNumber::-webkit-outer-spin-button,
#pageNumber::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type='number'] {
    -moz-appearance: textfield;
}
#headerLoadingBar {
    height: 3px;
    .progress {
        @apply bg-blue-600;
    }
}

@screen md {
    #viewerToolbar {
        input {
            padding-bottom: 0.25rem;
            padding-top: 0.25rem;
        }
    }
}
</style>
